import { ImageSourcePropType } from 'react-native';

/**
 * Persona avatar images for user profiles
 * Each avatar represents a different travel personality type
 * 
 * Image files are located in: assets/avatars/
 * To update avatars, replace the PNG files with your custom images.
 */
export const AVATAR_IMAGES = {
  traveler: 'traveler',
  casual: 'casual',
  stylish: 'stylish',
  sporty: 'sporty',
  classic: 'classic',
  modern: 'modern',
  urban: 'urban',
  chill: 'chill',
  adventurer: 'adventurer',
  scholar: 'scholar',
  compass: 'compass',
  harbor: 'harbor',
  sage: 'sage',
  trail: 'trail',
  sojourner: 'sojourner',
  ember: 'ember',
} as const;

export type AvatarId = keyof typeof AVATAR_IMAGES;

export type PresetAvatar = {
  id: AvatarId;
  image: ImageSourcePropType;
};

export const PRESET_AVATAR_IDS: AvatarId[] = [
  'traveler',
  'casual',
  'stylish',
  'sporty',
  'classic',
  'modern',
  'urban',
  'chill',
  'adventurer',
  'scholar',
  'compass',
  'harbor',
  'sage',
  'trail',
  'sojourner',
  'ember',
];

const avatarImageCache: Partial<Record<AvatarId, ImageSourcePropType>> = {};

function resolveAvatarImage(id: AvatarId): ImageSourcePropType {
  switch (id) {
    case 'traveler':
      return require('../../assets/avatars/traveler.png');
    case 'casual':
      return require('../../assets/avatars/casual.png');
    case 'stylish':
      return require('../../assets/avatars/stylish.png');
    case 'sporty':
      return require('../../assets/avatars/sporty.png');
    case 'classic':
      return require('../../assets/avatars/classic.png');
    case 'modern':
      return require('../../assets/avatars/modern.png');
    case 'urban':
      return require('../../assets/avatars/urban.png');
    case 'chill':
      return require('../../assets/avatars/chill.png');
    case 'adventurer':
      return require('../../assets/avatars/adventurer.png');
    case 'scholar':
      return require('../../assets/avatars/scholar.png');
    case 'compass':
      return require('../../assets/avatars/compass.png');
    case 'harbor':
      return require('../../assets/avatars/harbor.png');
    case 'sage':
      return require('../../assets/avatars/sage.png');
    case 'trail':
      return require('../../assets/avatars/trail.png');
    case 'sojourner':
      return require('../../assets/avatars/sojourner.png');
    case 'ember':
      return require('../../assets/avatars/ember.png');
  }
}

export function getPresetAvatars(): PresetAvatar[] {
  return PRESET_AVATAR_IDS.map((id) => ({
    id,
    image: getAvatarImage(id) as ImageSourcePropType,
  }));
}

/**
 * Get the image source for a preset avatar by ID
 */
export function getAvatarImage(id: string): ImageSourcePropType | null {
  if (id in AVATAR_IMAGES) {
    const avatarId = id as AvatarId;
    if (!avatarImageCache[avatarId]) {
      avatarImageCache[avatarId] = resolveAvatarImage(avatarId);
    }
    return avatarImageCache[avatarId] ?? null;
  }
  return null;
}

